import PageMeta from "../components/PageMeta";

export default function Terms() {
  return (
    <>
      <PageMeta
        title="Terms of Service · TruckTrackr"
        description="The terms governing your use of TruckTrackr — the food-truck discovery platform."
        canonical="https://thetrucktrackr.com/terms"
      />
      <article
        className="mx-auto max-w-3xl px-5 py-10 legal-prose"
        data-testid="terms-page"
      >
        <h1 className="font-display">Terms of Service</h1>
        <p className="text-sm text-muted-foreground">
          Effective date: June 1, 2026 · Last updated: June 1, 2026
        </p>

        <p>
          Welcome to TruckTrackr. These Terms of Service ("Terms") govern
          your use of <a href="https://thetrucktrackr.com">thetrucktrackr.com</a>{" "}
          (the "Service"), operated by TruckTrackr ("we", "us"). By using
          the Service, you agree to these Terms.
        </p>

        <h2>1. Eligibility &amp; accounts</h2>
        <p>
          You must be at least 13 years old to use the Service. If you
          create an account, you are responsible for maintaining the
          confidentiality of your credentials and for all activity under
          your account. Provide accurate information; keep it current.
        </p>

        <h2>2. Two user types</h2>
        <ul>
          <li>
            <strong>Customers</strong> — anyone using the Service to find
            food trucks. Free to use.
          </li>
          <li>
            <strong>Food-truck owners</strong> — operators who create a
            profile, publish a schedule, and (optionally) subscribe to
            our paid <em>Pro</em> tier for enhanced visibility and
            features.
          </li>
        </ul>

        <h2>3. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Post false, misleading, or fraudulent information.</li>
          <li>Impersonate another person or business.</li>
          <li>Upload content you don't have the rights to.</li>
          <li>
            Use the Service to harass, threaten, defame, or harm any
            person or business.
          </li>
          <li>
            Scrape, mass-download, or reverse-engineer the Service except
            as permitted by law.
          </li>
          <li>
            Interfere with the Service's operation (DDoS, malware, fake
            accounts, fake reviews).
          </li>
          <li>Violate any applicable law or regulation.</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate accounts that
          violate these Terms.
        </p>

        <h2>4. Owner content &amp; license</h2>
        <p>
          If you are a food-truck owner, you retain ownership of the
          content you upload (logos, menu, photos, descriptions). You
          grant TruckTrackr a worldwide, royalty-free, non-exclusive
          license to host, display, distribute, and promote that content
          on the Service and in marketing of the Service. You represent
          that you have all rights necessary to grant this license.
        </p>

        <h2>5. Pro subscriptions</h2>
        <ul>
          <li>
            Pro subscriptions are billed in advance, monthly or annually,
            via Stripe. Prices are shown at checkout.
          </li>
          <li>
            You may cancel anytime; cancellations take effect at the end
            of the current billing period. You retain Pro features until
            then.
          </li>
          <li>
            When you cancel, you keep Pro until the end of the period
            you&apos;ve already paid for, so partial-period refunds
            usually aren&apos;t needed and aren&apos;t automatic. If a
            charge ever looks wrong, email support@thetrucktrackr.com
            and we&apos;ll work with you to make it right — refunds for
            exceptional cases are handled individually at our discretion.
          </li>
          <li>
            We may change Pro pricing or features with at least 30 days'
            notice.
          </li>
        </ul>

        <h2>6. Third-party integrations</h2>
        <p>
          The Service integrates with third-party services (Google
          Calendar, Stripe, mapping providers). Your use of those
          third-party services is governed by their respective terms and
          privacy policies. We are not responsible for their conduct.
        </p>

        <h2>7. Disclaimers</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
          WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT
          LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT
          INFORMATION POSTED BY OWNERS (HOURS, ADDRESSES, MENU,
          AVAILABILITY) IS ACCURATE OR CURRENT.
        </p>

        <h2>8. Limitation of liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRUCKTRACKR SHALL NOT BE
          LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
          PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, OR DATA,
          ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE.
          OUR AGGREGATE LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE
          WILL NOT EXCEED THE AMOUNTS YOU PAID US IN THE 12 MONTHS
          PRECEDING THE CLAIM, OR $100, WHICHEVER IS GREATER.
        </p>

        <h2>9. Indemnification</h2>
        <p>
          You agree to indemnify and hold TruckTrackr harmless from any
          claims, damages, or expenses (including reasonable attorneys'
          fees) arising from your content, your use of the Service, or
          your violation of these Terms.
        </p>

        <h2>10. Termination</h2>
        <p>
          You may stop using the Service and delete your account at any
          time. We may suspend or terminate your account if you violate
          these Terms or for any other reason on reasonable notice.
        </p>

        <h2>11. Governing law &amp; disputes</h2>
        <p>
          These Terms are governed by the laws of the Commonwealth of
          Pennsylvania, USA, without regard to its conflict-of-laws rules.
          Any dispute will be resolved exclusively in the state or federal
          courts located in Lancaster County, Pennsylvania, and you
          consent to personal jurisdiction in those courts.
        </p>

        <h2>12. Changes</h2>
        <p>
          We may update these Terms from time to time. Material changes
          will be announced by email or in-app banner. Continued use of
          the Service after the effective date of the new Terms
          constitutes acceptance.
        </p>

        <h2>13. Contact</h2>
        <p>
          Questions? Email{" "}
          <a href="mailto:support@thetrucktrackr.com">
            support@thetrucktrackr.com
          </a>
          .
        </p>
      </article>
    </>
  );
}
